import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { SETTINGS_TRIGGER_SX, ShiftScopeChips } from "./ShiftScopeChips";

type Props = {
	shiftLabels: string[];
	enabledShiftLabels: string[];
	onToggleShift: (label: string) => void;
};

/**
 * The Move tab's Settings: the shift filter, and only that.
 *
 * The Move board is scoped by the same filter as everything else on the page, so without this the only
 * way to change which shift types it shows was to leave for the Hire tab — a board filtered by a
 * control that lives on another board. It is deliberately NOT the Hire popover with its sections
 * switched off: shift length and shifts-per-week size a suggested PERSON, and there are no people here
 * to size, so an optional-section prop would only exist to be unused half the time.
 */
const ShiftScopePopover: FC<Props> = ({ shiftLabels, enabledShiftLabels, onToggleShift }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	return (
		<SystemReactTinyPopover
			align="end"
			isOpen={open}
			onClickOutside={() => setOpen(false)}
			positions={["bottom", "top", "left"]}
			content={
				<Box onClick={(event) => event.stopPropagation()} data-testid="hiring-move-settings-panel" sx={{ width: 300, p: 2 }}>
					<ShiftScopeChips shiftLabels={shiftLabels} enabledShiftLabels={enabledShiftLabels} onToggleShift={onToggleShift} />
				</Box>
			}
		>
			<SystemButton
				buttonVariant="secondary"
				size="compact"
				sx={SETTINGS_TRIGGER_SX}
				noFullWidthMobile
				leadingIcon={<SettingsOutlinedIcon sx={{ fontSize: 14 }} />}
				leadingIconGap="6px"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
				data-testid="hiring-move-settings-button"
			>
				{t("hiring.hire.settings.title")}
			</SystemButton>
		</SystemReactTinyPopover>
	);
};

export default ShiftScopePopover;
